// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
//
// CardRewardToast — global, one-time-per-card popup that fires when
// the trading-card award reconciler grants a signed-in user a new
// card between visits (e.g. after a clean-path tournament win).
//
// Detection reuses the EXISTING award pipeline in routes/cards.py —
// this component only calls GET /api/cards/collection (which runs the
// pull-based reconciler on the server for the caller), diffs the
// returned owned-set against a per-user seen-set kept in localStorage,
// and pops one toast per newly-earned card_id.  Dismissing a toast
// (auto after ~8s or user click) marks that card_id as seen so it
// never fires again on subsequent page loads.
//
// This file exists strictly for the reward-toast feature; the
// underlying reward mechanics live in routes/cards.py and Cards.jsx
// and are NOT touched.
import { useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_PREFIX = "chess_card_reward_seen::";

function loadSeen(userId) {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + userId);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw));
  } catch {
    return new Set();
  }
}

function saveSeen(userId, seen) {
  try {
    localStorage.setItem(STORAGE_PREFIX + userId, JSON.stringify([...seen]));
  } catch {
    /* quota / disabled — silently degrade to per-tab suppression via ref */
  }
}

export default function CardRewardToast() {
  const { user, authEnabled } = useAuth();
  // Guard against double-fire in React strict-mode dev double-render.
  const firedThisSession = useRef(new Set());

  const check = useCallback(async () => {
    if (authEnabled && !user) return;
    if (!user?.user_id) return;
    let items;
    try {
      const { data } = await axios.get(`${API}/cards/collection`, {
        withCredentials: true,
      });
      items = data?.cards || [];
    } catch {
      // Silently skip — network hiccup should not spam errors on
      // every page load.  Next visit will retry.
      return;
    }
    const seen = loadSeen(user.user_id);
    // Sort newest-award first so if multiple cards land at once the
    // most recent one is popped last (top of the toast stack).
    const sorted = [...items].sort(
      (a, b) => (a.awarded_at || "").localeCompare(b.awarded_at || ""),
    );
    let changed = false;
    for (const c of sorted) {
      if (!c.card_id) continue;
      if (seen.has(c.card_id) || firedThisSession.current.has(c.card_id)) continue;
      seen.add(c.card_id);
      firedThisSession.current.add(c.card_id);
      changed = true;
      const label = c.name || c.label || c.card_id;
      toast.success(`You earned ${label}!`, {
        description:
          c.source === "auto_logo"
            ? "Champion Seal unlocked — your poster is complete."
            : "A new panel has been added to your collection.",
        duration: 8000,
        dismissible: true,
        // Sonner stacks toasts; we keep the default action-less
        // affordance so tapping the card body dismisses it.
      });
    }
    if (changed) saveSeen(user.user_id, seen);
  }, [user, authEnabled]);

  useEffect(() => {
    check();
    // Also refresh once the tab regains focus, so a card earned in
    // another tab / after backgrounding still surfaces on return.
    const onFocus = () => check();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [check]);

  return null;
}
